// The free plan needs a work email address (the server enforces it: `free_domain`). Once
// you have one you may monitor any site. One place for the wording and the small
// client-side hints, so every page says the same.

import type { Organization } from './api'
import { isFreeTier } from './subscription-access'

/** What the free plan asks for. */
export const FREE_DOMAIN_RULE = 'The free plan needs a work email address.'

/** The short form for a plan card bullet. */
export const FREE_DOMAIN_BULLET = 'Needs a work email. Paid plans take any email'

/** The line that goes next to it everywhere: the rule never applies to paid plans. */
export const PAID_ANY_EMAIL = 'Paid plans accept any email address.'

/** The 403 `code` the server sends when a free organization has no work email yet. */
export const DOMAIN_REQUIRED_CODE = 'domain_required'

/** What stands between a free user and a new monitor, in the order the server checks. */
export type CreateMonitorBlock = 'confirm_email' | 'work_email'

/** The short reason next to a disabled create button. The banner holds the full text and the actions. */
export const CREATE_BLOCK_REASON: Record<CreateMonitorBlock, string> = {
  confirm_email: 'Confirm your email address first.',
  work_email: 'Needs a confirmed work email, or Pro.',
}

/**
 * Only the server can say if the rule applies: it knows the age of the organization and the
 * addresses of every member. A build that does not send the field is not blocked here, so the
 * refusal from the server (`WorkEmailDialog`) stays the fallback.
 */
export function createMonitorBlock(
  organization: Organization | null,
  user: { email_confirmed?: boolean } | null | undefined,
): CreateMonitorBlock | null {
  if (!isFreeTier(organization)) return null
  if (user?.email_confirmed === false) return 'confirm_email'
  return organization?.work_email_required === true ? 'work_email' : null
}

// Hint only. The server decides: it knows the full list and the country domains.
const FREE_MAIL = new Set([
  'gmail.com', 'googlemail.com', 'outlook.com', 'hotmail.com', 'live.com', 'msn.com', 'yahoo.com', 'ymail.com',
  'icloud.com', 'me.com', 'aol.com', 'proton.me', 'protonmail.com', 'pm.me', 'gmx.com', 'gmx.de', 'gmx.net',
  'web.de', 'mail.com', 'mail.ru', 'yandex.com', 'yandex.ru', 'qq.com', '163.com', 'naver.com', 'zoho.com', 'hey.com',
])

/** True when the address is at a well-known free mail provider (so it is not a work email). */
export function looksLikeFreeMail(email: string): boolean {
  const at = email.trim().toLowerCase().lastIndexOf('@')
  if (at < 0) return false
  return FREE_MAIL.has(email.trim().toLowerCase().slice(at + 1))
}
